"use client";

import { supabase } from "@/lib/supabase";
import type { DbWidget } from "@/types/db";
import { useEffect, useState } from "react";

export type PinnedWidget = Pick<DbWidget, "id" | "name">;

// Capped at WIDGET_PIN_LIMIT, enforced by the caller (same convention as
// PROJECT_PIN_LIMIT).
export const WIDGET_PIN_LIMIT = 3;

export const WIDGET_PINS_CHANGED_EVENT = "woods:widget-pins-changed";

const notifyWidgetPinsChanged = () => {
  window.dispatchEvent(new Event(WIDGET_PINS_CHANGED_EVENT));
};

export async function fetchPinnedWidgets(userId: string): Promise<PinnedWidget[]> {
  const { data: pins, error: pinsError } = await supabase
    .from("widget_pins")
    .select("widget_id")
    .eq("user_id", userId)
    .order("pinned_at", { ascending: false });

  if (pinsError) {
    console.error("[fetchPinnedWidgets] pins fetch error:", pinsError);
    return [];
  }

  const widgetIds = (pins ?? []).map((pin) => pin.widget_id);
  if (widgetIds.length === 0) return [];

  const { data: widgets, error: widgetsError } = await supabase
    .from("widgets")
    .select("id, name")
    .in("id", widgetIds);

  if (widgetsError) {
    console.error("[fetchPinnedWidgets] widgets fetch error:", widgetsError);
    return [];
  }

  const byId = new Map(
    ((widgets as PinnedWidget[] | null) ?? []).map((widget) => [widget.id, widget])
  );
  return widgetIds.map((id) => byId.get(id)).filter((widget): widget is PinnedWidget => widget != null);
}

export async function pinWidget(userId: string, widgetId: string): Promise<boolean> {
  const { error } = await supabase
    .from("widget_pins")
    .insert({ user_id: userId, widget_id: widgetId });

  if (error) {
    console.error("[pinWidget] insert error:", error);
    return false;
  }
  notifyWidgetPinsChanged();
  return true;
}

export async function unpinWidget(userId: string, widgetId: string): Promise<boolean> {
  const { error } = await supabase
    .from("widget_pins")
    .delete()
    .eq("user_id", userId)
    .eq("widget_id", widgetId);

  if (error) {
    console.error("[unpinWidget] delete error:", error);
    return false;
  }
  notifyWidgetPinsChanged();
  return true;
}

export function usePinnedWidgets(userId: string | null | undefined): PinnedWidget[] {
  const [pinnedWidgets, setPinnedWidgets] = useState<PinnedWidget[]>([]);

  useEffect(() => {
    if (!userId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPinnedWidgets([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const pins = await fetchPinnedWidgets(userId);
      if (!cancelled) setPinnedWidgets(pins);
    };
    void load();
    window.addEventListener(WIDGET_PINS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(WIDGET_PINS_CHANGED_EVENT, load);
    };
  }, [userId]);

  return pinnedWidgets;
}
